import { useCallback, useEffect, useRef, useState } from 'react';
import { restoreSession } from '../httpClient';
import { errorText, reportClientError } from '../errorHandling';

export function useSession() {
  const [user, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState('');
  const mounted = useRef(false);
  const generation = useRef(0);

  // Refs and React setters are stable; imported helpers and callback-local
  // variables are not reactive dependencies. Generation guards reject stale work.
  const setUser = useCallback(nextUser => {
    generation.current += 1;
    setCurrentUser(nextUser);
    setSessionError('');
    setLoading(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const requestId = ++generation.current;
    const isCurrent = () => mounted.current && requestId === generation.current;
    try {
      const restored = await restoreSession();
      if (isCurrent()) { setCurrentUser(restored); setSessionError(''); }
    } catch (error) {
      if (!isCurrent()) return;
      if (error.response?.status === 401) {
        setCurrentUser(null);
        setSessionError('');
      } else {
        reportClientError('Unable to restore business session', error);
        setSessionError(errorText(error));
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refreshUser();
    return () => { mounted.current = false; generation.current += 1; };
  }, [refreshUser]);

  return { user, setUser, loading, sessionError, refreshUser };
}